import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  notifyNotificationCountChanged,
  type NotificationItem,
  type PageMeta,
} from '../socialApi';
import { notificationDestination, notificationSenderName, notificationText } from './actionability';
import { UserAvatar } from './UserAvatar';

function relativeTime(value: string | null): string {
  if (!value) return 'только что';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'недавно';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'только что';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

type NotificationGroup = 'Сегодня' | 'Вчера' | 'Раньше';

function notificationGroup(value: string | null): NotificationGroup {
  if (!value) return 'Раньше';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Раньше';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const itemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDay.getTime() === today.getTime()) return 'Сегодня';
  if (itemDay.getTime() === yesterday.getTime()) return 'Вчера';
  return 'Раньше';
}

type NotificationsScreenProps = {
  authenticated: boolean;
  onBack: () => void;
  onOpenUser: (userId: string) => void;
  onOpenVenue: (venueId: string) => void;
};

export function NotificationsScreen({ authenticated, onBack }: NotificationsScreenProps) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [meta, setMeta] = useState<(PageMeta & { unread_count: number }) | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextPage: number, append: boolean) => {
    if (!authenticated) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchNotifications(nextPage, 20);
      setItems((current) => append ? [...current, ...response.data] : response.data);
      setMeta(response.meta);
      setPage(nextPage);
    } catch {
      setError('Не удалось загрузить уведомления.');
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    setItems([]);
    setMeta(null);
    setPage(1);
    if (authenticated) void load(1, false);
  }, [authenticated, load]);

  const groupedItems = useMemo(() => {
    const groups: Record<NotificationGroup, NotificationItem[]> = {
      'Сегодня': [],
      'Вчера': [],
      'Раньше': [],
    };
    items.forEach((item) => groups[notificationGroup(item.created_at)].push(item));
    return (Object.entries(groups) as Array<[NotificationGroup, NotificationItem[]]>).filter(([, group]) => group.length > 0);
  }, [items]);

  const openItem = async (item: NotificationItem) => {
    if (!item.is_read) {
      try {
        await markNotificationRead(item.id);
        setItems((current) => current.map((candidate) => candidate.id === item.id
          ? { ...candidate, is_read: true, read_at: new Date().toISOString() }
          : candidate));
        setMeta((current) => current ? { ...current, unread_count: Math.max(0, current.unread_count - 1) } : current);
        notifyNotificationCountChanged();
      } catch {
        // Navigation remains available even when the read-state request fails.
      }
    }

    const destination = notificationDestination(item);
    if (destination) window.location.assign(destination);
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      const now = new Date().toISOString();
      setItems((current) => current.map((item) => ({ ...item, is_read: true, read_at: item.read_at ?? now })));
      setMeta((current) => current ? { ...current, unread_count: 0 } : current);
      notifyNotificationCountChanged();
    } catch {
      setError('Не удалось отметить уведомления прочитанными.');
    }
  };

  return (
    <main className="social-page notifications-screen">
      <button className="social-back" type="button" onClick={onBack}>← Назад</button>
      <section className="social-page__header">
        <h1>Уведомления</h1>
        {authenticated && (meta?.unread_count ?? 0) > 0 && (
          <button className="social-secondary-button" type="button" onClick={() => void markAll()}>Прочитать все</button>
        )}
      </section>

      {!authenticated ? (
        <section className="social-empty">
          <strong>Войдите, чтобы видеть уведомления</strong>
          <span>Уведомления доступны только в вашем профиле Хоппера.</span>
        </section>
      ) : error ? (
        <section className="social-empty is-error">
          <strong>{error}</strong>
          <button className="social-primary-button" type="button" onClick={() => void load(1, false)}>Повторить</button>
        </section>
      ) : items.length === 0 && !loading ? (
        <section className="social-empty"><strong>Пока нет уведомлений</strong></section>
      ) : (
        <div className="notification-groups">
          {groupedItems.map(([label, group]) => (
            <section className="notification-day-group" key={label}>
              <h2>{label}</h2>
              <div className="notification-list">
                {group.map((item) => (
                  <button
                    type="button"
                    className={item.is_read ? 'notification-row' : 'notification-row is-unread'}
                    key={item.id}
                    onClick={() => void openItem(item)}
                  >
                    <UserAvatar displayName={notificationSenderName(item)} size={48} />
                    <span className="notification-row__copy">
                      <strong>{notificationText(item)}</strong>
                      <small>{relativeTime(item.created_at)}</small>
                    </span>
                    {!item.is_read && <span className="notification-row__dot" aria-label="Непрочитанное" />}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {loading && <div className="social-loading">Загружаем…</div>}
      {meta && page < meta.last_page && !loading && (
        <button className="social-load-more" type="button" onClick={() => void load(page + 1, true)}>Показать ещё</button>
      )}
    </main>
  );
}
